import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { Message, Participant } from '../../../types/messaging-types';
import { DateUtils } from '../../../utils';
import { selectCurrentUser } from '../../user';
import { useSignalR } from '../hooks/useSignalR';
import {
  useGetConversationByIdQuery,
  useMarkConversationAsReadMutation,
  useSendMessageMutation,
} from '../messaging-api-slice';
import { ConversationSearch } from './conversation-search';
import { MessageInput } from './message-input';

const MESSAGES_PER_PAGE = 50;

interface ConversationViewProps {
  conversationId: number;
  onMessageSent?: () => void;
  onInfoClick?: (participant: Participant) => void;
}

export const ConversationView: React.FC<ConversationViewProps> = ({
  conversationId,
  onMessageSent,
  onInfoClick,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const hasScrolledToBottomRef = useRef<boolean>(false);
  const isUserScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLoadedConversationIdRef = useRef<number | null>(null);
  const currentUser = useAppSelector(selectCurrentUser);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [optimisticMessages, setOptimisticMessages] = useState<
    Array<{ id: string; content: string; sentAt: string; isSending: boolean }>
  >([]);
  const [isSending, setIsSending] = useState(false);
  const [loadedMessagesCount, setLoadedMessagesCount] = useState(MESSAGES_PER_PAGE);
  const [allLoadedMessages, setAllLoadedMessages] = useState<Message[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const {
    data: conversation,
    isLoading,
    isFetching,
    refetch,
  } = useGetConversationByIdQuery(
    {
      id: conversationId,
      skip: 0,
      take: loadedMessagesCount,
    },
    {
      refetchOnMountOrArgChange: true, // Always refetch when conversationId changes
    }
  );

  const [sendMessage] = useSendMessageMutation();
  const [markAsRead] = useMarkConversationAsReadMutation();

  // Set up SignalR for real-time messaging
  const { connection } = useSignalR(conversationId);

  // Listen for new messages via SignalR
  useEffect(() => {
    if (!connection) return;

    const handleMessageReceived = (message: Message) => {
      // Only add message if it's not already in our list and belongs to this conversation
      if (message.conversationId === conversationId) {
        const isOwnMessage = conversation?.currentUserId === message.senderId;

        // If this is our own message, remove the optimistic message immediately
        // (optimistic messages are in a separate state, not in allLoadedMessages)
        if (isOwnMessage) {
          setOptimisticMessages(prevOpt => prevOpt.filter(m => m.content !== message.content));
        }

        setAllLoadedMessages(prev => {
          // Check if message already exists by ID
          const existsById = prev.some(m => m.id === message.id);
          if (existsById) {
            return prev; // Already have it, don't add duplicate
          }

          // If this is our own message and we already removed the optimistic one,
          // just add the real message
          if (isOwnMessage) {
            return [...prev, message];
          }

          // Message from someone else - add it
          return [...prev, message];
        });

        // Scroll to bottom if user is near bottom
        setTimeout(() => {
          if (messagesContainerRef.current && !isUserScrollingRef.current) {
            const container = messagesContainerRef.current;
            const isNearBottom =
              container.scrollHeight - container.scrollTop - container.clientHeight < 150;
            if (isNearBottom) {
              container.scrollTop = container.scrollHeight;
            }
          }
        }, 100);
      }
    };

    connection.on('MessageReceived', handleMessageReceived);

    return () => {
      connection.off('MessageReceived', handleMessageReceived);
    };
  }, [connection, conversationId, conversation?.currentUserId]);

  // Reset loaded messages when conversation changes
  useEffect(() => {
    // Clear all state when conversation changes to prevent flicker
    setAllLoadedMessages([]);
    setLoadedMessagesCount(MESSAGES_PER_PAGE);
    setIsLoadingMore(false);
    setOptimisticMessages([]);
    setIsSending(false);
    setIsInitializing(true);
    lastLoadedConversationIdRef.current = null;
    scrollPositionRef.current = null;
    hasScrolledToBottomRef.current = false;
    isUserScrollingRef.current = false;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, [conversationId]);

  // Update loaded messages when conversation data changes
  useEffect(() => {
    // Only update if we have conversation data and it matches the current conversationId
    if (conversation && conversation.id === conversationId && conversation.messages !== undefined) {
      const isNewConversation = lastLoadedConversationIdRef.current !== conversation.id;

      setAllLoadedMessages(prev => {
        // If we're switching conversations (prev is empty or belongs to different conversation), replace entirely
        const shouldReplace =
          prev.length === 0 ||
          (prev.length > 0 && prev[0].conversationId !== conversation.id) ||
          isNewConversation;

        if (shouldReplace) {
          // Initial load or conversation switch - use the messages from the API
          lastLoadedConversationIdRef.current = conversation.id;
          return conversation.messages || [];
        } else {
          // Loading more messages in the same conversation - prepend older messages
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = (conversation.messages || []).filter(m => !existingIds.has(m.id));
          // Prepend older messages (they're already sorted oldest to newest)
          const combined = [...newMessages, ...prev];
          return combined.sort(
            (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
          );
        }
      });
    }
  }, [conversation, conversationId]);

  // Clear initializing when conversation loads AND messages are loaded
  // Wait for messages to be processed before clearing to prevent showing "No messages" prematurely
  useEffect(() => {
    // Only clear if:
    // 1. Conversation matches the ID
    // 2. Not loading/fetching
    // 3. Messages have been loaded into allLoadedMessages (or conversation explicitly has no messages)
    if (
      conversation &&
      conversation.id === conversationId &&
      !isLoading &&
      !isFetching &&
      conversation.messages !== undefined &&
      (allLoadedMessages.length > 0 || conversation.messages.length === 0)
    ) {
      setIsInitializing(false);
    }
  }, [conversation, conversationId, isLoading, isFetching, allLoadedMessages.length]);

  // Combine loaded messages with optimistic messages
  const allMessages = useMemo(() => {
    // Filter out optimistic messages that already exist in allLoadedMessages (by content)
    // This prevents duplicates when SignalR delivers the message before we remove the optimistic one
    const existingContents = new Set(allLoadedMessages.map(m => m.content));
    const filteredOptimistic = optimisticMessages.filter(opt => !existingContents.has(opt.content));

    // Add optimistic messages at the end
    // Note: Optimistic messages have string IDs, but we'll use a temporary number for type compatibility
    const optimistic = filteredOptimistic.map(
      opt =>
        ({
          id: -1, // Temporary negative ID - will be replaced when SignalR delivers the real message
          conversationId,
          senderId: conversation?.currentUserId || 0,
          senderName: currentUser
            ? `${currentUser.firstName} ${currentUser.lastName}`.trim() || 'You'
            : 'You',
          senderAvatar: currentUser?.avatar,
          recipientId: conversation?.otherParticipant.id || 0,
          content: opt.content,
          sentAt: opt.sentAt,
          isRead: false,
          isSending: opt.isSending,
          _optimisticId: opt.id, // Store original optimistic ID for matching
        }) as unknown as Message & { isSending?: boolean; _optimisticId?: string }
    );

    // Combine and sort by sentAt
    const combined = [...allLoadedMessages, ...optimistic];
    return combined.sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
  }, [
    allLoadedMessages,
    optimisticMessages,
    conversation?.currentUserId,
    conversation?.otherParticipant?.id,
    conversationId,
    currentUser,
  ]);

  // Filter messages by search term
  const filteredMessages = useMemo(() => {
    if (!allMessages.length) return [];
    if (!searchTerm.trim()) return allMessages;

    const term = searchTerm.toLowerCase();
    return allMessages.filter((message: Message & { isSending?: boolean }) =>
      message.content.toLowerCase().includes(term)
    );
  }, [allMessages, searchTerm]);

  // Mark conversation as read when it's loaded
  useEffect(() => {
    if (conversation && conversation.messages.length > 0) {
      const hasUnread = conversation.messages.some(m => !m.isRead);
      if (hasUnread) {
        markAsRead(conversationId);
      }
    }
  }, [conversation, conversationId, markAsRead]);

  // Scroll to bottom ONLY when sending new messages (optimistic updates)
  useEffect(() => {
    if (!messagesContainerRef.current || isLoadingMore || isUserScrollingRef.current) return;

    const container = messagesContainerRef.current;

    // ONLY scroll when sending a new message (optimistic)
    // Do NOT auto-scroll if user is manually scrolling
    if (optimisticMessages.length > 0) {
      setTimeout(() => {
        if (container && !isUserScrollingRef.current) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    }
  }, [optimisticMessages.length, isLoadingMore]);

  // Scroll to bottom when conversation is first loaded (only once per conversation)
  useEffect(() => {
    if (!conversation || hasScrolledToBottomRef.current || isLoadingMore || isInitializing) return;

    // Wait for messages to be loaded and UI to be ready
    if (allLoadedMessages.length > 0 && allLoadedMessages.length <= MESSAGES_PER_PAGE) {
      // Use requestAnimationFrame for smoother scroll without flicker
      // Double RAF ensures DOM is fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (
            messagesContainerRef.current &&
            !hasScrolledToBottomRef.current &&
            conversation.id === conversationId &&
            allLoadedMessages.length > 0
          ) {
            // Scroll immediately without animation to prevent flicker
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            hasScrolledToBottomRef.current = true;
          }
        });
      });
    }
  }, [conversation, conversationId, allLoadedMessages.length, isLoadingMore, isInitializing]);

  // Track scroll position to determine if user is at bottom
  // This is only for tracking - we don't auto-scroll based on this
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current || isLoadingMore) return;

    // Mark that user is actively scrolling
    isUserScrollingRef.current = true;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Reset the flag after user stops scrolling for 150ms
    scrollTimeoutRef.current = setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 150);

    // Track if user is near bottom (stored in ref for scroll behavior decisions)
    const container = messagesContainerRef.current;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    hasScrolledToBottomRef.current = isNearBottom;
  }, [isLoadingMore]);

  const handleLoadMore = async () => {
    if (!messagesContainerRef.current || isLoading) return;

    setIsLoadingMore(true);

    // Store current scroll position BEFORE loading
    const container = messagesContainerRef.current;
    scrollPositionRef.current = {
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    };

    const newCount = loadedMessagesCount + MESSAGES_PER_PAGE;
    setLoadedMessagesCount(newCount);

    // The query will automatically refetch with new pagination params
    // We'll restore scroll position after messages are loaded
  };

  // Restore scroll position after loading more messages
  useEffect(() => {
    if (isLoadingMore && !isLoading && messagesContainerRef.current && scrollPositionRef.current) {
      const container = messagesContainerRef.current;
      const stored = scrollPositionRef.current;

      // Wait for DOM to update with new messages
      // Use requestAnimationFrame for better timing
      const restoreScroll = () => {
        if (container && stored) {
          requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDifference = newScrollHeight - stored.scrollHeight;

            // Adjust scroll position to maintain the same relative position
            // Since we're prepending messages at the top, we need to shift scroll down
            if (heightDifference > 0) {
              container.scrollTop = stored.scrollTop + heightDifference;
            }

            scrollPositionRef.current = null;
            setIsLoadingMore(false);
          });
        }
      };

      // Try multiple times with increasing delays to ensure DOM is ready
      setTimeout(restoreScroll, 100);
      setTimeout(restoreScroll, 300);
      setTimeout(restoreScroll, 500);
    }
  }, [isLoadingMore, isLoading, allLoadedMessages.length]);

  const handleSendMessage = async (content: string) => {
    if (!conversation || isSending) return;

    const optimisticId = `optimistic-${Date.now()}-${Math.random()}`;
    const optimisticMessage = {
      id: optimisticId,
      content,
      sentAt: new Date().toISOString(),
      isSending: true,
    };

    // Add optimistic message immediately
    setOptimisticMessages(prev => [...prev, optimisticMessage]);
    setIsSending(true);

    try {
      await sendMessage({
        conversationId,
        request: {
          recipientId: conversation.otherParticipant.id,
          content,
        },
      }).unwrap();

      // Don't remove optimistic message yet - let SignalR handle the update
      // SignalR will replace the optimistic message with the real one
      // Only remove if SignalR doesn't deliver within 2 seconds (fallback)
      setTimeout(() => {
        setOptimisticMessages(prev => {
          const stillOptimistic = prev.find(m => m.id === optimisticId);
          if (stillOptimistic) {
            // SignalR didn't deliver, remove optimistic and refetch as fallback
            refetch();
            return prev.filter(m => m.id !== optimisticId);
          }
          return prev;
        });
      }, 2000);
      // Scroll to bottom after sending (user is at bottom when they send)
      // Only scroll if user is still near bottom and not manually scrolling
      setTimeout(() => {
        if (messagesContainerRef.current && !isUserScrollingRef.current) {
          const container = messagesContainerRef.current;
          const isNearBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight < 150;
          if (isNearBottom) {
            container.scrollTop = container.scrollHeight;
          }
        }
      }, 100);
      onMessageSent?.();
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove failed optimistic message
      setOptimisticMessages(prev => prev.filter(m => m.id !== optimisticId));
      // TODO: Show error toast/notification to user
    } finally {
      setIsSending(false);
    }
  };

  const formatMessageTime = (dateString: string, alwaysIncludeDate: boolean = false): string => {
    const date = new Date(dateString);
    const now = new Date();

    // Reset time to midnight for date comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.floor((nowOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));

    const timeString = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    // Always include date if requested, or if it's not today
    if (alwaysIncludeDate || diffDays !== 0) {
      if (diffDays === 0) {
        return `Today ${timeString}`;
      } else if (diffDays === 1) {
        return `Yesterday ${timeString}`;
      } else {
        return `${DateUtils.toDisplayDate(dateString)} ${timeString}`;
      }
    }

    return timeString;
  };

  if (isLoading || isInitializing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading conversation...
        </Typography>
      </Box>
    );
  }

  if (!conversation) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Conversation not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Paper sx={{ p: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={conversation.otherParticipant.avatar}
              alt={conversation.otherParticipant.name}
              sx={{ width: 48, height: 48 }}
            >
              {conversation.otherParticipant.name.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="h6">{conversation.otherParticipant.name}</Typography>
            {onInfoClick && (
              <Tooltip title="View participant information">
                <IconButton size="small" onClick={() => onInfoClick(conversation.otherParticipant)}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ flexShrink: 0 }} />

      {/* Search within conversation */}
      <Box sx={{ flexShrink: 0 }}>
        <ConversationSearch value={searchTerm} onChange={setSearchTerm} />
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* Messages - This container must have a constrained height to enable scrolling */}
      <Box
        ref={messagesContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 2,
          minHeight: 0,
          position: 'relative',
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
        }}
      >
        {allMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        ) : filteredMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No messages found matching "{searchTerm}"
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Load More button at the top */}
            {!searchTerm && conversation?.hasMoreMessages && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={20} /> : 'Load Older Messages'}
                </Button>
              </Box>
            )}
            <div ref={messagesTopRef} />
            {filteredMessages.map((message: Message) => {
              // Use the currentUserId from the conversation detail (database ID)
              const isOwnMessage = conversation.currentUserId === message.senderId;

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: 1,
                    justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                  }}
                >
                  {!isOwnMessage && (
                    <Avatar
                      src={message.senderAvatar}
                      alt={message.senderName}
                      sx={{ width: 36, height: 36, flexShrink: 0 }}
                    >
                      {message.senderName.charAt(0).toUpperCase()}
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                      maxWidth: '70%',
                    }}
                  >
                    <Paper
                      sx={{
                        p: 1.5,
                        backgroundColor: isOwnMessage ? 'primary.light' : 'grey.100',
                        color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
                        opacity: (message as Message & { isSending?: boolean }).isSending ? 0.7 : 1,
                      }}
                    >
                      {!isOwnMessage && (
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                        >
                          {message.senderName}
                        </Typography>
                      )}
                      <Typography variant="body1">{message.content}</Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.25,
                          mt: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.7,
                          }}
                        >
                          {formatMessageTime(message.sentAt, true)}
                        </Typography>
                        {isOwnMessage && (
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.7,
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            {(message as Message & { isSending?: boolean }).isSending ? (
                              <>
                                <CircularProgress size={10} sx={{ opacity: 0.7 }} />
                                Sending...
                              </>
                            ) : message.isRead ? (
                              message.readAt ? (
                                `Read at ${formatMessageTime(message.readAt, true)}`
                              ) : (
                                'Read'
                              )
                            ) : (
                              'Sent'
                            )}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                  {isOwnMessage && (
                    <Avatar
                      src={currentUser?.avatar}
                      alt={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'You'}
                      sx={{ width: 36, height: 36, flexShrink: 0 }}
                    >
                      {currentUser
                        ? `${currentUser.firstName?.charAt(0) || ''}${currentUser.lastName?.charAt(0) || ''}`.toUpperCase()
                        : 'Y'}
                    </Avatar>
                  )}
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* Message Input */}
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <MessageInput onSend={handleSendMessage} disabled={isSending} />
      </Box>
    </Box>
  );
};
